/**
 * Google Picker, loaded on demand.
 *
 * https://apis.google.com/js/api.js is injected only the first time a user
 * opens the picker (never on page load), then gapi.load('picker'). The picker
 * runs with a short-lived drive.file-scoped access token minted by the backend
 * (GET /api/integrations/cloud-files/google/picker-token), so the browser
 * never holds the refresh token.
 */

const GAPI_SRC = 'https://apis.google.com/js/api.js';

export interface PickedGoogleFile {
  id: string;
  name: string;
  mimeType: string | null;
  url: string | null;
  sizeBytes: number | null;
}

export interface OpenGooglePickerOptions {
  apiKey: string;
  appId: string;
  accessToken: string;
  title?: string;
  locale?: string;
}

// Accessed through `any`: ProviderMap.tsx already declares Window.google (Maps).
const w = () => window as any;

let gapiPromise: Promise<void> | null = null;
let pickerPromise: Promise<void> | null = null;

function loadGapi(): Promise<void> {
  if (w().gapi) return Promise.resolve();
  if (gapiPromise) return gapiPromise;
  gapiPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GAPI_SRC}"]`);
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      gapiPromise = null;
      reject(new Error('Could not load Google Picker'));
    });
    if (!existing) {
      script.src = GAPI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return gapiPromise;
}

export function loadGooglePicker(): Promise<void> {
  if (w().google?.picker) return Promise.resolve();
  if (pickerPromise) return pickerPromise;
  pickerPromise = loadGapi().then(
    () =>
      new Promise<void>((resolve, reject) => {
        w().gapi.load('picker', {
          callback: () => resolve(),
          onerror: () => {
            pickerPromise = null;
            reject(new Error('Could not load Google Picker'));
          },
        });
      }),
  );
  pickerPromise.catch(() => {
    pickerPromise = null;
  });
  return pickerPromise;
}

/** For tests: forget the cached loader promises. */
export function __resetGooglePickerLoader() {
  gapiPromise = null;
  pickerPromise = null;
}

/** Opens the picker; resolves with the picked files ([] when cancelled). */
export async function openGooglePicker(opts: OpenGooglePickerOptions): Promise<PickedGoogleFile[]> {
  await loadGooglePicker();
  const gp = w().google.picker;
  const R = gp.Response ?? {};
  const D = gp.Document ?? {};
  const A = gp.Action ?? {};

  return new Promise<PickedGoogleFile[]>((resolve) => {
    const view = new gp.DocsView(gp.ViewId?.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false);
    let builder = new gp.PickerBuilder()
      .addView(view)
      .enableFeature(gp.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(opts.accessToken)
      .setDeveloperKey(opts.apiKey)
      .setAppId(opts.appId)
      .setOrigin(window.location.origin)
      .setCallback((data: Record<string, any>) => {
        const action = data[R.ACTION ?? 'action'];
        if (action === (A.PICKED ?? 'picked')) {
          const docs: any[] = data[R.DOCUMENTS ?? 'docs'] || [];
          resolve(
            docs.map((d) => ({
              id: String(d[D.ID ?? 'id']),
              name: String(d[D.NAME ?? 'name'] ?? ''),
              mimeType: d[D.MIME_TYPE ?? 'mimeType'] ?? null,
              url: d[D.URL ?? 'url'] ?? null,
              sizeBytes: d[D.SIZE_BYTES ?? 'sizeBytes'] != null ? Number(d[D.SIZE_BYTES ?? 'sizeBytes']) : null,
            })),
          );
        } else if (action === (A.CANCEL ?? 'cancel')) {
          resolve([]);
        }
      });
    if (opts.title && typeof builder.setTitle === 'function') builder = builder.setTitle(opts.title);
    if (opts.locale && typeof builder.setLocale === 'function') builder = builder.setLocale(opts.locale);
    builder.build().setVisible(true);
  });
}
